import React from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FaInstagram, FaTelegramPlane, FaWhatsapp, FaFacebook } from "react-icons/fa";
import { connect } from 'react-redux';
import { RootState } from '../../../models/RootState';
import { SiteConfig } from '../../../models/SiteConfig';
import { getDynamicTextColors } from '../../../store/authActions';
import { useTranslation } from '../../../hooks/useTranslation';

interface ContactUsProps {
  siteConfig?: SiteConfig;
  userDetails?: any;
}

const ContactUs: React.FC<ContactUsProps> = ({ siteConfig, userDetails }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get dynamic text color classes based on different backgrounds
  const { pageTextColorClass: textColorClass, inputBoxTextColorClass } = getDynamicTextColors(userDetails);

  const handleBackClick = () => {
    navigate(-1);
  };

  // Helper function to format Instagram link
  const getInstagramLink = (instagram: string): string => {
    try {
      const raw = instagram.trim();
      return raw.startsWith("http") ? raw : `https://instagram.com/${raw}`;
    } catch (err) {
      return "#";
    }
  };

  // Helper function to format WhatsApp link
  const getWhatsAppLink = (whatsapp: string): string => {
    try {
      const raw = whatsapp.trim();
      return raw.startsWith("http") ? raw : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
    } catch (err) {
      return "#";
    }
  };

  // Helper function to format Telegram link
  const getTelegramLink = (telegram: string): string => {
    try {
      const tg = telegram.trim();
      let link = tg;
      if (!tg.startsWith('t.me') && !tg.startsWith('http')) {
        link = `t.me/${tg}`;
      }
      return link.startsWith('http') ? link : `https://${link}`;
    } catch (err) {
      return "#";
    }
  };

  // Helper function to format Facebook link
  const getFacebookLink = (facebook: string): string => {
    try {
      const raw = facebook.trim();
      return raw.startsWith("http") ? raw : `https://facebook.com/${raw}`;
    } catch (err) {
      return "#";
    }
  };

  // Handle link click for Telegram WebApp
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, link: string) => {
    try {
      if (window.Telegram?.WebApp?.openLink) {
        e.preventDefault();
        window.Telegram.WebApp.openLink(link);
      }
    } catch (err) {
      console.warn("Link blocked or invalid:", err);
    }
  };

  const hasContactInfo = siteConfig?.contactInfo && (
    siteConfig.contactInfo.Instagram ||
    siteConfig.contactInfo.WhatsappSupport ||
    siteConfig.contactInfo.WhatsappSignup ||
    siteConfig.contactInfo.TelegramChannel ||
    siteConfig.contactInfo.Facebook
  );

  return (
    <div className={`flex flex-col px-3 mt-4 w-full pb-[7rem] py-[4rem] contact-us-page ${textColorClass}`}>
      <header className={`flex gap-3 items-center self-start text-base font-medium text-center mb-[0.5rem] ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
        <div>
          <div 
            className="bg-inputbox-color flex p-1 mr-[0.25rem] rounded cursor-pointer"
            onClick={() => handleBackClick()}
          >
            <ArrowBackIcon
              className={`aspect-square w-[18px] ${inputBoxTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
              style={{ width: '20px', height: '20px' }}
            />
          </div>
        </div>
        <h1 className="text-[1.2rem] font-medium">Contact Us</h1>
      </header>

      {/* Description Section */}
      <div className={`mt-4 mb-6 ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
        <p className="text-sm sm:text-base leading-relaxed opacity-90">
          We're here to help! Get in touch with us through any of the channels below. 
          Our support team is available to assist you with any questions or concerns you may have.
        </p>
      </div>

      {/* Contact Methods Section */}
      {hasContactInfo ? (
        <div className="space-y-4 mt-6">
          {/* Instagram */}
          {siteConfig?.contactInfo?.Instagram && (
            <a
              href={getInstagramLink(siteConfig.contactInfo.Instagram)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => handleLinkClick(e, getInstagramLink(siteConfig.contactInfo.Instagram))}
              className={`flex items-center gap-4 p-4 rounded-xl bg-inputbox-color border border-border-color hover:opacity-80 transition-all cursor-pointer w-full ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(to bottom right, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
              >
                <FaInstagram size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <h3 className="font-semibold text-base mb-1">Instagram</h3>
                <p className="text-sm opacity-80 break-all" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{siteConfig.contactInfo.Instagram}</p>
              </div>
            </a>
          )}

          {/* WhatsApp Support */}
          {siteConfig?.contactInfo?.WhatsappSupport && (
            <a
              href={getWhatsAppLink(siteConfig.contactInfo.WhatsappSupport)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => handleLinkClick(e, getWhatsAppLink(siteConfig.contactInfo.WhatsappSupport))}
              className={`flex items-center gap-4 p-4 rounded-xl bg-inputbox-color border border-border-color hover:opacity-80 transition-all cursor-pointer ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--whatsapp-color)' }}
              >
                <FaWhatsapp size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-base mb-1">WhatsApp Support</h3>
                <p className="text-sm opacity-80">Get instant support via WhatsApp</p>
              </div>
            </a>
          )}

          {/* WhatsApp Signup */}
          {siteConfig?.contactInfo?.WhatsappSignup && (
            <a
              href={getWhatsAppLink(siteConfig.contactInfo.WhatsappSignup)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => handleLinkClick(e, getWhatsAppLink(siteConfig.contactInfo.WhatsappSignup))}
              className={`flex items-center gap-4 p-4 rounded-xl bg-inputbox-color border border-border-color hover:opacity-80 transition-all cursor-pointer ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--whatsapp-color)' }}
              >
                <FaWhatsapp size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-base mb-1">WhatsApp Signup</h3>
                <p className="text-sm opacity-80">Get your account ID via WhatsApp</p>
              </div>
            </a>
          )}

          {/* Telegram Channel */}
          {siteConfig?.contactInfo?.TelegramChannel && (
            <a
              href={getTelegramLink(siteConfig.contactInfo.TelegramChannel)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => handleLinkClick(e, getTelegramLink(siteConfig.contactInfo.TelegramChannel))}
              className={`flex items-center gap-4 p-4 rounded-xl bg-inputbox-color border border-border-color hover:opacity-80 transition-all cursor-pointer w-full ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--telegram-color)' }}
              >
                <FaTelegramPlane size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <h3 className="font-semibold text-base mb-1">Telegram Channel</h3>
                <p className="text-sm opacity-80 break-all" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{siteConfig.contactInfo.TelegramChannel}</p>
              </div>
            </a>
          )}

          {/* Facebook */}
          {siteConfig?.contactInfo?.Facebook && (
            <a
              href={getFacebookLink(siteConfig.contactInfo.Facebook)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => handleLinkClick(e, getFacebookLink(siteConfig.contactInfo.Facebook))}
              className={`flex items-center gap-4 p-4 rounded-xl bg-inputbox-color border border-border-color hover:opacity-80 transition-all cursor-pointer w-full ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#1877F2' }}
              >
                <FaFacebook size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <h3 className="font-semibold text-base mb-1">Facebook</h3>
                <p className="text-sm opacity-80 break-all" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{siteConfig.contactInfo.Facebook}</p>
              </div>
            </a>
          )}
        </div>
      ) : (
        <div className={`mt-6 p-6 rounded-xl bg-inputbox-color border border-border-color ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
          <p className="text-center opacity-80">
            Contact information is not available at the moment. Please check back later.
          </p>
        </div>
      )}

      {/* Additional Help Section */}
      {siteConfig?.contactInfo?.EnableCustomerSupport && (
        <div className={`mt-8 p-4 rounded-xl bg-inputbox-color border border-border-color ${textColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
          <h3 className="font-semibold text-base mb-2">Need More Help?</h3>
          <p className="text-sm opacity-80 mb-4">
            You can also create a support ticket for detailed assistance.
          </p>
          <button
            onClick={() => navigate('/customersupport')}
            className="px-4 py-2 rounded-lg bg-primary-color text-white font-medium hover:opacity-90 transition-opacity"
          >
            Go to Customer Support
          </button>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    siteConfig: state.userDetails?.siteConfig,
    userDetails: state.userDetails?.userDetails,
  };
};

export default connect(mapStateToProps)(ContactUs);
